/**
 * Materials PDF Generation Service
 * 
 * Generates PDF documents for practice problems and study guides
 */

import jsPDF from 'jspdf';

export interface PDFGenerationConfig {
    title: string;
    content: string;
    topic: string;
    type: 'practice-problems' | 'study-guide';
    author?: string;
}

/**
 * Generate a PDF from practice problems or study guide content
 */
export async function generateMaterialPDF(config: PDFGenerationConfig): Promise<Blob> {
    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 25;
    const innerMargin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    let pageNumber = 0;

    // Helper function to add new page with footer
    const addPageFooter = () => {
        const footerY = pageHeight - 15;
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(120, 120, 120);
        pdf.text(`Page ${pageNumber}`, pageWidth / 2, footerY, { align: 'center' });
        pdf.text('Generated by Luca AI', margin, footerY);
        pdf.text(new Date().toLocaleDateString(), pageWidth - margin, footerY, { align: 'right' });
        pdf.setTextColor(0, 0, 0);
    };

    // Helper function to add new page if needed
    const checkAddPage = (requiredSpace: number = 15) => {
        if (yPosition + requiredSpace > pageHeight - 30) {
            addPageFooter();
            pdf.addPage();
            pageNumber++;
            yPosition = margin + 10;
            return true;
        }
        return false;
    };

    // Helper function to add text with word wrap
    const addWrappedText = (
        text: string, 
        x: number, 
        y: number, 
        maxWidth: number, 
        fontSize: number = 11,
        fontStyle: 'normal' | 'bold' = 'normal'
    ): number => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        
        const lines = pdf.splitTextToSize(text, maxWidth);
        const lineHeight = fontSize * 0.4;
        
        lines.forEach((line: string, index: number) => {
            checkAddPage(lineHeight + 5);
            pdf.text(line, x, y + (index * lineHeight));
            yPosition = y + ((index + 1) * lineHeight);
        });
        
        return yPosition;
    };

    // ===== PROFESSIONAL TITLE PAGE =====
    pageNumber++;
    
    // Decorative top border
    pdf.setFillColor(37, 99, 235);
    pdf.rect(0, 0, pageWidth, 3, 'F');
    
    // Title section with elegant spacing
    yPosition = 60;
    pdf.setTextColor(37, 99, 235);
    pdf.setFontSize(32);
    pdf.setFont('helvetica', 'bold');
    const mainTitle = config.type === 'practice-problems' ? 'Practice Problems' : 'Comprehensive Study Guide';
    pdf.text(mainTitle, pageWidth / 2, yPosition, { align: 'center' });
    
    // Subtitle/Topic
    yPosition += 15;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(60, 60, 60);
    pdf.text(config.topic, pageWidth / 2, yPosition, { align: 'center', maxWidth: maxWidth - 20 });
    
    // Decorative line
    yPosition += 12;
    pdf.setDrawColor(37, 99, 235);
    pdf.setLineWidth(0.5);
    pdf.line(pageWidth / 2 - 30, yPosition, pageWidth / 2 + 30, yPosition);
    
    // Author/Attribution box
    yPosition += 25;
    pdf.setFillColor(245, 247, 250);
    pdf.roundedRect(margin + 15, yPosition, maxWidth - 30, 30, 3, 3, 'F');
    
    yPosition += 10;
    pdf.setFontSize(11);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(80, 80, 80);
    pdf.text('Prepared by', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 7;
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(37, 99, 235);
    pdf.text('Luca AI Educational Platform', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 6;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(120, 120, 120);
    pdf.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), pageWidth / 2, yPosition, { align: 'center' });
    
    // Description box for study guides
    if (config.type === 'study-guide') {
        yPosition += 35;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const description = 'This comprehensive study guide has been carefully crafted to provide in-depth coverage of key concepts, practical examples, and structured learning materials to support your educational journey.';
        const descLines = pdf.splitTextToSize(description, maxWidth - 40);
        descLines.forEach((line: string) => {
            pdf.text(line, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 5;
        });
    }
    
    // Footer on title page
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'italic');
    pdf.setTextColor(150, 150, 150);
    pdf.text('Professional Study Materials', pageWidth / 2, pageHeight - 20, { align: 'center' });
    pdf.text('© Luca AI - All Rights Reserved', pageWidth / 2, pageHeight - 15, { align: 'center' });
    
    // Reset
    pdf.setTextColor(0, 0, 0);
    
    // Start content on new page
    pdf.addPage();
    pageNumber++;
    yPosition = margin + 10;

    // Process content
    const sections = config.content.split('\n');
    
    for (const section of sections) {
        const trimmedSection = section.trim();
        
        if (!trimmedSection) {
            yPosition += 3;
            continue;
        }

        checkAddPage();

        // Handle different markdown-style formatting
        if (trimmedSection.startsWith('##')) {
            const heading = trimmedSection.replace(/^##\s*/, '');
            checkAddPage(20);
            yPosition += 5;
            // Heading background
            pdf.setFillColor(37, 99, 235);
            pdf.roundedRect(margin - 2, yPosition - 5, maxWidth + 4, 12, 1, 1, 'F');
            // Heading text
            pdf.setFontSize(18);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(255, 255, 255);
            pdf.text(heading, margin + 3, yPosition + 3);
            yPosition += 15;
            pdf.setTextColor(0, 0, 0);
        } else if (trimmedSection.startsWith('###')) {
            const heading = trimmedSection.replace(/^###\s*/, '');
            checkAddPage(12);
            yPosition += 4;
            pdf.setFontSize(13);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(37, 99, 235);
            pdf.text(heading, margin + 2, yPosition);
            yPosition += 8;
            pdf.setTextColor(0, 0, 0);
        } else if (trimmedSection.startsWith('#')) {
            const heading = trimmedSection.replace(/^#\s*/, '');
            checkAddPage(14);
            yPosition += 3;
            pdf.setFontSize(15);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(50, 50, 50);
            pdf.text(heading, margin, yPosition);
            // Underline
            pdf.setDrawColor(37, 99, 235);
            pdf.setLineWidth(0.3);
            pdf.line(margin, yPosition + 1, margin + 40, yPosition + 1);
            yPosition += 9;
            pdf.setTextColor(0, 0, 0);
            
        } else if (trimmedSection.startsWith('**') && trimmedSection.endsWith('**')) {
            // Bold text
            yPosition += 2;
            const boldText = trimmedSection.replace(/\*\*/g, '');
            yPosition = addWrappedText(boldText, margin, yPosition, maxWidth, 11, 'bold');
            yPosition += 3;
            
        } else if (trimmedSection.startsWith('- ') || trimmedSection.startsWith('* ')) {
            // Bullet point
            checkAddPage(8);
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            pdf.circle(margin + 2, yPosition - 1.5, 0.8, 'F');
            yPosition = addWrappedText(trimmedSection.substring(2), margin + 6, yPosition, maxWidth - 6, 11);
            yPosition += 2;
            
        } else if (/^\d+\./.test(trimmedSection)) {
            // Numbered list
            checkAddPage(8);
            const match = trimmedSection.match(/^(\d+)\.\s*(.*)/);
            if (match) {
                pdf.setFontSize(11);
                pdf.setFont('helvetica', 'bold');
                pdf.text(match[1] + '.', margin, yPosition);
                pdf.setFont('helvetica', 'normal');
                yPosition = addWrappedText(match[2], margin + 8, yPosition, maxWidth - 8, 11);
                yPosition += 2;
            }
            
        } else if (trimmedSection.startsWith('```')) {
            // Code block (skip the markers)
            continue;
            
        } else if (trimmedSection === '---') {
            // Horizontal rule
            yPosition += 3;
            checkAddPage(5);
            pdf.setDrawColor(200, 200, 200);
            pdf.line(margin, yPosition, pageWidth - margin, yPosition);
            yPosition += 5;
            
        } else {
            // Regular paragraph
            checkAddPage(8);
            // Remove markdown bold/italic
            const cleanText = trimmedSection.replace(/\*\*/g, '').replace(/\*/g, '');
            yPosition = addWrappedText(cleanText, margin, yPosition, maxWidth, 11);
            yPosition += 4;
        }
    }

    // Add footer to all pages
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(128, 128, 128);
        pdf.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: 'center' }
        );
        pdf.text(
            'Generated by Luca AI - Your Elite CPA Reviewer',
            pageWidth - margin,
            pageHeight - 10,
            { align: 'right' }
        );
    }

    // Return as blob
    return pdf.output('blob');
}

/**
 * Download PDF with proper filename
 */
export function downloadMaterialPDF(blob: Blob, topic: string, type: 'practice-problems' | 'study-guide') {
    const timestamp = new Date().toISOString().split('T')[0];
    const cleanTopic = topic.replace(/[^a-z0-9]/gi, '-').toLowerCase();
    const typeName = type === 'practice-problems' ? 'practice-problems' : 'study-guide';
    const filename = `luca-${typeName}-${cleanTopic}-${timestamp}.pdf`;
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
